"use client";

import { useEffect, useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import QRCode from "qrcode";
import Helpers from "../Helper/Helpers";
import { useAuth } from "../context/AuthContextAdmin";

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchProducts, setAddtocartProduct, addtoCartProduct } = useAuth();

  const [cartItems, setCartItems] = useState(location.state || []);
  const [selectedPayment, setSelectedPayment] = useState("");
  const [qr, setQr] = useState("");
  const [status, setStatus] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    country: "India",
    streetAddress: "",
    city: "",
    state: "",
    pinCode: "",
    phone: "",
    email: "",
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const updateQuantity = (id, change) => {
    setCartItems((items) =>
      items.map((item) =>
        item._id === id
          ? { ...item, quantity: Math.max(1, item.quantity + change) }
          : item
      )
    );
  };

  const removeItem = async (id) => {
    try {
      const res = await Helpers(`/admin/product/${id}/toggle-cart`, "PUT");
      if (res?.status) {
        toast.success(res?.message);
        setCartItems((prevItems) =>
          prevItems.filter((item) => item._id !== id)
        );
        setAddtocartProduct(addtoCartProduct - 1);
      } else {
        toast.error("Failed to remove item");
      }
    } catch (error) {
      console.error("Error removing item", error);
      toast.error("An error occurred while removing the item");
    }
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const total = subtotal;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = Object.entries(formData).every(([key, value]) => {
      if (key === "orderNotes" || key === "shipToDifferent") return true;
      return value.trim() !== "";
    });

    if (!isValid) {
      toast.error("All fields are required!");
      return;
    }

    try {
      const res = await Helpers("/admin/order/", "POST", {
        userDetails: formData,
        product: cartItems,
        total: total,
      });

      if (res) {
        setAddtocartProduct(0);
        Swal.fire({
          title:
            "Thank you for your order! Our team will contact you shortly for confirmation.",
          icon: "success",
        });

        navigate("/");
      } else {
        toast.error("Order submission failed!");
      }
    } catch (error) {
      toast.error("An error occurred while submitting your order");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 mt-8">
      <form className="flex flex-col lg:flex-row gap-8" onSubmit={handleSubmit}>
        {/* Left Side: Billing Form */}
        <div className="lg:w-2/3 bg-white shadow rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">Billing Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="text"
              name="phone"
              placeholder="Phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="text"
              name="streetAddress"
              placeholder="Street Address"
              value={formData.streetAddress}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="text"
              name="city"
              placeholder="City"
              value={formData.city}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="text"
              name="state"
              placeholder="State"
              value={formData.state}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
            <input
              type="text"
              name="pinCode"
              placeholder="Pin Code"
              value={formData.pinCode}
              onChange={handleInputChange}
              className="border p-2 rounded"
            />
          </div>
        </div>

        {/* Right Side: Cart Items */}
        <div className="lg:w-1/3 bg-white shadow rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">Your Cart</h2>
          {cartItems.length === 0 ? (
            <p>Your cart is empty.</p>
          ) : (
            <>
              {cartItems.map((item) => (
                <div
                  key={item._id}
                  className="flex items-center justify-between border-b py-2"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={item.images[0]}
                      alt={item.name}
                      className="w-16 h-16 object-contain"
                    />
                    <div>
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-sm text-gray-500">₹{item.price}</p>
                      <div className="flex items-center mt-1">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item._id, -1)}
                          className="p-1 border rounded"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item._id, 1)}
                          className="p-1 border rounded"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item._id)}
                    className="text-red-500"
                  >
                    <X />
                  </button>
                </div>
              ))}

              <div className="mt-4">
                <p className="text-lg font-semibold">Subtotal: ₹{subtotal}</p>
                <p className="text-lg font-semibold">Total: ₹{total}</p>
              </div>

              <button
                type="submit"
                className="mt-6 bg-blue-600 text-white px-6 py-2 rounded w-full sm:w-auto sm:ml-auto"
              >
                Submit Order
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
